import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  alpha,
  Box,
  Chip,
  FormHelperText,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { NumberFormatValues, NumericFormat } from 'react-number-format';

import {
  RECURRING_ANONYMIZATION_CADENCE_DAYS_FIELD,
  RECURRING_ANONYMIZATION_CADENCE_HELP_TEXT,
  RECURRING_ANONYMIZATION_CADENCE_LABEL,
  RECURRING_ANONYMIZATION_CADENCE_PRESETS,
  RECURRING_ANONYMIZATION_DEFAULT_CADENCE_DAYS,
  RECURRING_ANONYMIZATION_ENABLED_FIELD,
  RECURRING_ANONYMIZATION_MAX_CADENCE_DAYS,
  RECURRING_ANONYMIZATION_MIN_CADENCE_DAYS,
} from '../../../../constants/organizationsKeys';
import { isCadenceUnsetByBackend, isValidCadence } from '../../../../utils/organizations';
import Toggle from '../../inputs/Toggle/Toggle';
import { InputLabel } from '../../labels';

const stepperButtonSx = {
  width: 48,
  height: '100%',
  borderRadius: 0,
  color: 'primary.main',
  '&.Mui-disabled': { color: 'action.disabled' },
};

interface IProps {
  enabled: boolean;
  cadenceDays?: number | null;
  disabled?: boolean;
  error?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onChange: (name: string, value: boolean | number | null) => void;
}

const RecurringAnonymizationSection: React.FC<IProps> = ({
  enabled,
  cadenceDays,
  disabled,
  error,
  onBlur,
  onChange,
}) => {
  const isCadenceDisabled = useMemo(() => !!disabled || !enabled, [disabled, enabled]);

  const displayedCadence = useMemo(() => {
    if (enabled) {
      return cadenceDays ?? null;
    }

    return isValidCadence(cadenceDays) ? cadenceDays : RECURRING_ANONYMIZATION_DEFAULT_CADENCE_DAYS;
  }, [enabled, cadenceDays]);

  const handleToggleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.currentTarget;

      onChange(RECURRING_ANONYMIZATION_ENABLED_FIELD, checked);

      const isUnset = cadenceDays === null || cadenceDays === undefined || isCadenceUnsetByBackend(cadenceDays);

      if (!isValidCadence(cadenceDays) && (checked || !isUnset)) {
        onChange(RECURRING_ANONYMIZATION_CADENCE_DAYS_FIELD, RECURRING_ANONYMIZATION_DEFAULT_CADENCE_DAYS);
      }
    },
    [onChange, cadenceDays],
  );

  const handleCadenceChange = useCallback(
    (value?: number | null) => {
      if (isCadenceDisabled) {
        return;
      }

      onChange(RECURRING_ANONYMIZATION_CADENCE_DAYS_FIELD, value ?? null);
    },
    [onChange, isCadenceDisabled],
  );

  const handleTypedCadence = useCallback(
    ({ floatValue }: NumberFormatValues) => handleCadenceChange(floatValue),
    [handleCadenceChange],
  );

  const stepCadence = useCallback(
    (delta: 1 | -1) =>
      handleCadenceChange(
        displayedCadence === null || displayedCadence < RECURRING_ANONYMIZATION_MIN_CADENCE_DAYS
          ? RECURRING_ANONYMIZATION_MIN_CADENCE_DAYS
          : displayedCadence + delta,
      ),
    [displayedCadence, handleCadenceChange],
  );

  const handlePresetClick = useCallback((preset: number) => () => handleCadenceChange(preset), [handleCadenceChange]);

  const isDecreaseDisabled = useMemo(
    () => isCadenceDisabled || (displayedCadence ?? 0) <= RECURRING_ANONYMIZATION_MIN_CADENCE_DAYS,
    [isCadenceDisabled, displayedCadence],
  );
  const isIncreaseDisabled = useMemo(
    () => isCadenceDisabled || (displayedCadence ?? 0) >= RECURRING_ANONYMIZATION_MAX_CADENCE_DAYS,
    [isCadenceDisabled, displayedCadence],
  );

  return (
    <Stack spacing={3} justifyContent="space-between" alignSelf="stretch" minWidth={0}>
      <Box width="fit-content">
        <Toggle
          name={RECURRING_ANONYMIZATION_ENABLED_FIELD}
          helperText="Scheduled anonymization"
          checked={enabled}
          disabled={disabled}
          onChange={handleToggleChange}
          dataAction="toggle_recurring_anonymization_enabled"
        />
      </Box>
      <Box
        data-testid="recurring-anonymization-cadence"
        sx={{ opacity: isCadenceDisabled ? 0.6 : 1, transition: 'opacity 0.25s ease' }}
      >
        <InputLabel value={RECURRING_ANONYMIZATION_CADENCE_LABEL} />
        <Stack direction="row" alignItems="center" flexWrap="wrap" columnGap={2} rowGap={1.5}>
          <NumericFormat
            customInput={TextField}
            value={displayedCadence ?? ''}
            valueIsNumericString={false}
            allowNegative={false}
            allowLeadingZeros={false}
            decimalScale={0}
            onValueChange={handleTypedCadence}
            onBlur={onBlur}
            disabled={isCadenceDisabled}
            name={RECURRING_ANONYMIZATION_CADENCE_DAYS_FIELD}
            error={!!error}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: 58,
                px: 0,
                borderRadius: '10px',
                bgcolor: isCadenceDisabled ? 'grey.50' : 'common.white',
                transition: 'background-color 0.25s ease',
              },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: error ? 'error.main' : 'grey.300' },
              '& .MuiInputAdornment-root': { height: '100%', maxHeight: 'none', m: 0 },
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: (theme: Theme) => theme.palette.text.disabled,
                color: 'text.disabled',
              },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      aria-label="Decrease by 1 day"
                      onClick={() => stepCadence(-1)}
                      disabled={isDecreaseDisabled}
                      sx={stepperButtonSx}
                    >
                      <RemoveIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Increase by 1 day"
                      onClick={() => stepCadence(1)}
                      disabled={isIncreaseDisabled}
                      sx={stepperButtonSx}
                    >
                      <AddIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              },
              htmlInput: {
                'aria-label': RECURRING_ANONYMIZATION_CADENCE_LABEL,
                maxLength: RECURRING_ANONYMIZATION_MAX_CADENCE_DAYS.toString().length,
                sx: {
                  width: 72,
                  height: '100%',
                  boxSizing: 'border-box',
                  px: 0.75,
                  py: 0,
                  borderLeft: 1,
                  borderRight: 1,
                  borderColor: 'grey.100',
                  textAlign: 'center',
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  color: 'grey.900',
                },
              },
            }}
          />
          <Typography sx={{ fontSize: '1.1rem', lineHeight: 1.15, color: 'text.disabled' }}>days ago</Typography>
          <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} flexBasis="100%">
            {RECURRING_ANONYMIZATION_CADENCE_PRESETS.map((preset) => {
              const isSelected = displayedCadence === preset;

              return (
                <Chip
                  key={preset}
                  component="button"
                  clickable
                  label={preset}
                  variant="outlined"
                  color={isSelected ? 'primary' : 'default'}
                  onClick={handlePresetClick(preset)}
                  disabled={isCadenceDisabled}
                  aria-pressed={isSelected}
                  data-action="cadence_preset"
                  sx={{
                    minWidth: 44,
                    height: 'auto',
                    py: 0.875,
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    lineHeight: 1.15,
                    '& .MuiChip-label': { px: 1.5 },
                    ...(isSelected
                      ? { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) }
                      : { borderColor: 'transparent', bgcolor: 'common.white', color: 'grey.900' }),
                    '&.Mui-disabled': { opacity: 1, color: isSelected ? undefined : 'text.disabled' },
                  }}
                />
              );
            })}
          </Stack>
        </Stack>
        <FormHelperText
          error={!!error}
          role={error ? 'alert' : undefined}
          sx={{ mt: 1.25, mx: 0, fontSize: '0.875rem', lineHeight: 1.15, ...(!error && { color: 'text.disabled' }) }}
        >
          {error ?? RECURRING_ANONYMIZATION_CADENCE_HELP_TEXT}
        </FormHelperText>
      </Box>
    </Stack>
  );
};

export default RecurringAnonymizationSection;
